import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import {
  ticketStatusLabel, ticketCategoryLabel, ticketStatusVariant, formatIso,
} from "../../lib/tickets";

function MessageBubble({ msg }) {
  const isCustomer = msg.sender_role === "customer";
  return (
    <div
      className={`flex ${isCustomer ? "justify-end" : "justify-start"} mb-3`}
      data-testid={`ticket-msg-${msg.message_id}`}
    >
      <div
        className={`max-w-2xl rounded-lg px-4 py-3 shadow-sm border ${
          isCustomer
            ? "bg-orange-50 border-orange-100 text-zinc-900"
            : "bg-white border-zinc-200 text-zinc-900"
        }`}
      >
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="overline text-[10px]">
            {isCustomer ? "YOU" : "SUPPORT"}
          </div>
          <div className="font-mono text-[10px] text-zinc-400">
            {formatIso(msg.created_at)}
          </div>
        </div>
        <div className="text-sm whitespace-pre-wrap">{msg.body}</div>
        <div className="text-[10px] text-zinc-400 mt-1 font-mono">
          {msg.sender_email}
        </div>
      </div>
    </div>
  );
}

export default function CustomerSupportTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const reload = () => {
    setLoading(true);
    api.get(`/portal/support-tickets/${ticketId}`)
      .then(({ data }) => setTicket(data))
      .catch((e) => {
        if (e?.response?.status === 403) {
          toast.error("You don't have access to that ticket.");
          navigate("/portal/support");
        } else if (e?.response?.status === 404) {
          toast.error("Ticket not found.");
          navigate("/portal/support");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(reload, [ticketId]);
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  const submit = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/portal/support-tickets/${ticketId}/reply`,
        { body: reply.trim() },
      );
      setTicket(data);
      setReply("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply.");
    } finally {
      setSending(false);
    }
  };

  if (loading || !ticket) {
    return (
      <div className="gf-card">
        <div className="gf-card-body text-sm text-zinc-400 text-center py-10">
          Loading ticket…
        </div>
      </div>
    );
  }

  const isClosed = ticket.status === "closed";

  return (
    <>
      <div className="mb-3">
        <Link
          to="/portal/support"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800"
          data-testid="ticket-back"
        >
          <ArrowLeft size={12} /> All tickets
        </Link>
      </div>
      <PageHeader
        title={ticket.subject}
        subtitle={`PORTAL / SUPPORT / ${ticket.ticket_id}`}
        testId="ticket-detail-header"
        actions={
          <Badge variant={ticketStatusVariant(ticket.status)} data-testid="ticket-detail-status">
            {ticketStatusLabel(ticket.status)}
          </Badge>
        }
      />

      <div className="gf-card mb-4">
        <div className="gf-card-body grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="overline text-[10px]">CATEGORY</div>
            <div>{ticketCategoryLabel(ticket.category)}</div>
          </div>
          <div>
            <div className="overline text-[10px]">OPENED</div>
            <div className="font-mono">{formatIso(ticket.opened_at)}</div>
          </div>
          <div>
            <div className="overline text-[10px]">LAST UPDATE</div>
            <div className="font-mono">{formatIso(ticket.last_message_at)}</div>
          </div>
          <div>
            <div className="overline text-[10px]">MESSAGES</div>
            <div>{ticket.message_count || ticket.messages?.length || 0}</div>
          </div>
        </div>
      </div>

      <div className="gf-card mb-4">
        <div
          className="gf-card-body max-h-[60vh] overflow-y-auto bg-zinc-50/30"
          data-testid="ticket-thread"
        >
          {(ticket.messages || []).map((m) => (
            <MessageBubble key={m.message_id} msg={m} />
          ))}
          <div ref={endRef} />
        </div>
      </div>

      {isClosed ? (
        <div
          className="gf-card border-l-4 border-zinc-300 bg-zinc-50/40"
          data-testid="ticket-closed-notice"
        >
          <div className="gf-card-body text-sm text-zinc-600">
            This ticket is closed. Open a new ticket if you need further help.
          </div>
        </div>
      ) : (
        <div className="gf-card">
          <div className="gf-card-body">
            <label className="overline text-[10px]">REPLY</label>
            <textarea
              rows={4}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              className="gf-input w-full"
              placeholder="Type your reply…"
              data-testid="ticket-reply-body"
            />
            <div className="mt-3 flex justify-end">
              <button
                onClick={submit}
                disabled={sending || !reply.trim()}
                className="gf-btn gf-btn-primary"
                data-testid="ticket-reply-submit"
              >
                <PaperPlaneTilt size={14} weight="fill" />
                {sending ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
